<script lang="ts" setup>
import type { OtTabType } from '@1trading/design-system';
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import persistanceHelper from '@exchange/helpers/persistance-helper';
import { accountService } from '@exchange/libs/account/service/src';
import { authService } from '@exchange/libs/auth/service/src';
import BalancesListExchange from '@exchange/libs/balances/ui/src/lib/balance-list/BalancesListExchange.vue';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { usePromiseWithCB } from '@exchange/libs/composables/shared/src/lib/usePromise';
import { ordersService } from '@exchange/libs/order/my-orders/service/src';
import { getNumberOfActiveOrdersToCancel } from '@exchange/libs/order/my-orders/ui/src/lib/useOrdersList';
import { MyOrdersListType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { settingsService } from '@exchange/libs/settings/service/src';
import { SubaccountType } from '@exchange/libs/trading-accounts/service/src';

import FilledOrders from './filled/FilledOrders.vue';
import OpenOrders from './open/OpenOrders.vue';
import OpenPositions from './positions/OpenPositions.vue';

type MyOrderTypeSwitcher = OtTabType<MyOrdersListType>;

defineProps({
  dragging: { type: Boolean, default: false },
  marketId: { type: String, required: true },
});

const emit = defineEmits<(e: 'my-orders:scroll-content', value: number) => void>();

const tabsPersistance = {
  localstorageKey: 'PRO_MO_ACTIVE_TAB',
  defaultValue: MyOrdersListType.open,
};

const { t } = useI18n({ useScope: 'global' });
const { isXS } = useMQBreakpoints();

const orderNumberToCancel = getNumberOfActiveOrdersToCancel();
const { loading: cancellingAll, createPromise: cancelAllOrders } = usePromiseWithCB(ordersService.cancelAllOrders, true);

const container = ref();
const containerTopPosition = ref(0);
const moveBack = ref(false);

const { subaccountType } = accountService;

const isFuturesOrIsLoadingSubaccount = computed(() => !subaccountType.value || subaccountType.value === SubaccountType.FUTURES);

const myOrderTypeOptions = ref([
  ...(isFuturesOrIsLoadingSubaccount.value
    ? [
        {
          getTitle: () => t('modules.myOrders.tabs.positions'),
          label: computed(() => t('modules.myOrders.tabs.positions')),
          id: MyOrdersListType.positions,
          active: false,
        },
      ]
    : []),
  {
    getTitle: () => t('modules.myOrders.tabs.openOrders'),
    label: computed(() => t('modules.myOrders.tabs.openOrders')),
    id: MyOrdersListType.open,
    active: true,
  },
  {
    getTitle: () => t('modules.myOrders.tabs.filled'),
    label: computed(() => t('modules.myOrders.tabs.filled')),
    id: MyOrdersListType.filled,
    active: false,
  },
  ...(isFuturesOrIsLoadingSubaccount.value
    ? [
        {
          getTitle: () => t('modules.myOrders.tabs.balances'),
          label: computed(() => t('modules.myOrders.tabs.balances')),
          id: MyOrdersListType.balances,
          active: false,
        },
      ]
    : []),
] satisfies Array<MyOrderTypeSwitcher>);

const selectedMyOrderType = ref(myOrderTypeOptions.value.find((e) => e.active));

const showOpenOrders = computed(() => {
  const valueToCompare = !isXS.value ? myOrderTypeOptions.value.find((e) => e.active)?.id : selectedMyOrderType.value?.id;

  return valueToCompare === MyOrdersListType.open;
});
const showAllMarkets = computed(() => Boolean(settingsService.settings.userSettings.myOrdersSettings.showOrdersFromAllMarkets));

const ordersSnapshotsReceived = computed(() => ordersService.snapshotReceived[showOpenOrders.value ? MyOrdersListType.open : MyOrdersListType.filled]);

const isAuthenticated = computed(() => authService.hasBeenAuthenticated);
const loading = computed(() => {
  if (!authService.initialLoadAuthIsChecked) {
    return true;
  }

  if (authService.hasBeenAuthenticated) {
    return !ordersSnapshotsReceived.value;
  }

  return false;
});

const onTabClicked = () => {
  if (isXS.value && container.value?.$el) {
    emit('my-orders:scroll-content', containerTopPosition.value);
  }
};

const onMyOrderTypeSelectClick = () => {
  if (selectedMyOrderType.value) {
    selectedMyOrderType.value.active = true;
  }

  onTabClicked();
};

const onMyOrderTypeTabClick = (tab: MyOrderTypeSwitcher) => {
  moveBack.value = tab.id === MyOrdersListType.open;
  onTabClicked();
};

const updateSettings = async (value: boolean) => {
  await settingsService.setSettings({
    path: 'myOrdersSettings.showOrdersFromAllMarkets',
    value,
  });
};

const setContainerTopPosition = () => {
  const { top } = container.value.$el.getBoundingClientRect();

  containerTopPosition.value = top;
};

const activeComponent = computed(() => {
  const activeTabId = !isXS.value ? myOrderTypeOptions.value.find((e) => e.active)?.id : selectedMyOrderType.value?.id;

  switch (activeTabId) {
    case MyOrdersListType.open:
      return OpenOrders;
    case MyOrdersListType.filled:
      return FilledOrders;
    case MyOrdersListType.balances:
      return BalancesListExchange;
    case MyOrdersListType.positions:
      return OpenPositions;
    default:
      return OpenOrders; // Default to OpenOrders if no match
  }
});

const ensurePersistedTabIsValid = () => {
  const persistedTab = persistanceHelper.getObjFromJSON(tabsPersistance.localstorageKey, tabsPersistance.defaultValue);

  if (persistedTab && !myOrderTypeOptions.value.find((e) => e.id === persistedTab)) {
    persistanceHelper.localStorageDelete(tabsPersistance.localstorageKey);
  }
};

onBeforeMount(ensurePersistedTabIsValid);

onMounted(setContainerTopPosition);
</script>

<template>
  <ot-module-container
    ref="container"
    class="my-orders-container"
    testid="my-orders"
    actions-class="my-orders-container__actions"
    :loading="loading"
    :toolbar-class="isXS ? 'my-orders-container__toolbar--sticky' : ''"
  >
    <template #header-left-actions>
      <div class="my-orders-container__type-switcher-wrap">
        <ot-select
          v-if="isXS"
          v-model:value="selectedMyOrderType"
          :disabled="!isAuthenticated"
          :narrow="true"
          :options="myOrderTypeOptions"
          :short="true"
          label="label"
          variant="dark-outlined"
          @update:value="onMyOrderTypeSelectClick"
        >
          <template #selected-option="selected">
            <x-list-item :title="selected.label" />
          </template>
        </ot-select>
        <ot-tabs
          v-else
          v-model:tabs="myOrderTypeOptions"
          :disabled="!isAuthenticated"
          :persistance="tabsPersistance"
          :dense="true"
          @tab-selected="onMyOrderTypeTabClick"
        />
      </div>
    </template>
    <template #header-actions>
      <div class="my-orders-container__actions-right">
        <ot-checkbox
          v-if="activeComponent !== BalancesListExchange"
          class="my-orders-container__show-all"
          :selected="showAllMarkets"
          :label="$t('modules.myOrders.settings.showAllMarkets')"
          :disabled="!isAuthenticated"
          :vertically-centered="true"
          @update:selected="updateSettings"
        />
        <ot-button-dropdown
          v-if="activeComponent === OpenOrders && isXS"
          class="my-orders-container__cancel-all-dropdown"
          :activator-icon-rotated="true"
          :disabled="!showOpenOrders || !isAuthenticated"
          size="3xs"
          variant="icon"
        >
          <template #options>
            <x-list-item
              :title="$t('modules.myOrders.cancelAllOrders')"
              @click="cancelAllOrders"
            />
          </template>
        </ot-button-dropdown>
        <x-button
          v-else-if="activeComponent === OpenOrders"
          class="my-orders-container__cancel-all-button"
          variant="tertiary"
          size="2xs"
          :loading="cancellingAll"
          :disabled="!orderNumberToCancel || !showOpenOrders || !isAuthenticated"
          :text="$t('modules.myOrders.cancelAllOrders')"
          @click="cancelAllOrders"
        />
      </div>
    </template>
    <template #main-content>
      <ot-transition-fade
        :move-back="moveBack"
        :push="true"
        :is-xs="false"
      >
        <component
          :is="activeComponent"
          :history-source="{ isInstant: false }"
          :dragging="dragging"
          :market-id="marketId"
          :show-all-markets="showAllMarkets"
          :authenticated-user="isAuthenticated"
        />
      </ot-transition-fade>
    </template>
  </ot-module-container>
</template>

<style lang="scss">
.my-orders-container {
  position: relative;
  display: flex;

  .xs & {
    .scroll-container {
      overflow: initial;
    }
  }

  &__toolbar {
    &--sticky {
      position: sticky;
      z-index: var(--toolbar-sticky-header-z-index);
      top: 0;
      background-color: var(--xmc-background-color);
    }
  }

  &__actions {
    display: flex;
    align-items: center;
  }

  &__cancel-all-button {
    gap: 5px;
  }

  &__actions-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__show-all {
    font-size: var(--font-size-xs);
  }
}
</style>
